import React from 'react'
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { ChartData } from '../../types'
import { useContainerDimensions } from '../../hooks/useContainerDimensions'
import { useChartColors, useThemeColors } from '../../hooks/useThemeColors'

interface PieChartComponentProps {
  data: ChartData[]
  loading?: boolean
  error?: string | null
}

const PieChartComponent: React.FC<PieChartComponentProps> = ({
  data = [],
  loading = false,
  error = null
}) => {
  const [containerRef, dimensions] = useContainerDimensions()
  const colors = useThemeColors()
  const chartColors = useChartColors()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Device Usage Distribution
      </Typography>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          backgroundColor: colors.background.paper,
          width: '100%',
          height: isMobile ? 350 : 400,
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            flex: 1,
            minHeight: 0,
            position: 'relative'
          }}>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: colors.text.secondary
              }}>
              <CircularProgress />
              <Typography variant='body1' sx={{ mt: 2 }}>
                Loading chart data...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity='error'>Failed to load chart data: {error}</Alert>
          )}

          {!loading && !error && (
            <PieChart width={dimensions.width} height={dimensions.height}>
              <Pie
                data={data}
                cx='50%'
                cy='50%'
                labelLine={false}
                outerRadius={
                  Math.min(dimensions.width, dimensions.height) *
                  (isMobile ? 0.25 : 0.3)
                }
                fill='#8884d8'
                dataKey='value'
                nameKey='name'
                // Simple string label without function to avoid type issues
                label={({ percent }: any) => `${(percent * 100).toFixed(0)}%`}>
                {data.map(
                  (
                    _,
                    index // Use underscore to indicate unused parameter
                  ) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={chartColors[index % chartColors.length]}
                    />
                  )
                )}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value} (${((Number(value) / total) * 100).toFixed(1)}%)`,
                  name
                ]}
                contentStyle={{
                  backgroundColor: colors.background.paper,
                  borderColor: colors.divider,
                  color: colors.text.primary
                }}
              />
              <Legend
                wrapperStyle={{
                  fontSize: isMobile ? '12px' : '14px',
                  paddingTop: isMobile ? '10px' : '20px'
                }}
              />
            </PieChart>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default PieChartComponent
